import {
  CAPTION_AIR,
  CARD_BOTTOM_OFFSET,
  CARD_MAX_HEIGHT,
  CARD_PADDING,
  CARD_WIDTH,
  ORB_VISUAL_TOP,
} from "../presence";
import type { ChromeCopy } from "../uiText";

/**
 * DOM-ul și CSS-ul cardului lichid. Textul stă PESTE forma desenată în canvas
 * (`presence/orb.ts` → `drawMorph`), nu într-o cutie cu fundal propriu - de-aia
 * cardul n-are `background` decât în varianta plată, cea fără canvas.
 *
 * Lățimea/înălțimea conținutului se derivă din geometria formei, importată din
 * `presence/index.ts`: două constante identice în două fișiere ar diverge la
 * prima reglare, iar textul ar ieși din sticlă.
 */

/** Lățimea coloanei de text: forma minus marginile ei de sticlă. */
const CONTENT_WIDTH = CARD_WIDTH - 52;
/** Conținutul nu are voie să iasă din forma plafonată. */
const CONTENT_MAX_HEIGHT = CARD_MAX_HEIGHT - 22;
/** Muchia de jos a formei + jumătate din aer: textul stă centrat în sticlă. */
const CONTENT_BOTTOM = CARD_BOTTOM_OFFSET + CARD_PADDING / 2;

/**
 * Umbra sticlei, în termeni numiți: bula o folosește ca să știe CÂT iese lumina
 * ei sub propria muchie, iar regula CSS de mai jos o desenează din aceleași
 * două numere. Un `box-shadow: 0 Ypx BLURpx` se stinge la Y+BLUR sub cutie.
 */
const GLASS_SHADOW_Y = 10;
const GLASS_SHADOW_BLUR = 30;

/**
 * Distanța de la baza ferestrei până la muchia de JOS a bulei de voce.
 *
 * Cât timp vocea vorbește, orbul NU se morfează: el dansează, iar replica
 * plutește deasupra lui. Deci bula are exact problema caption-ului din
 * `presence/index.ts` - să nu urce peste ansamblul orbului - și se rezolvă cu
 * aceiași termeni numiți, importați, nu copiați: `ORB_VISUAL_TOP` (halo-ul la
 * energie maximă în `speaking`, calculat din `orbRadius()` real) + cât iese
 * umbra bulei sub propria cutie + `CAPTION_AIR` (aerul liber).
 *
 * Diferă de `CAPTION_BOTTOM_OFFSET` doar prin termenul din mijloc: caption-ul e
 * text cu `text-shadow`, bula e sticlă cu `box-shadow` - două scurgeri diferite
 * ale aceleiași geometrii.
 */
const BUBBLE_BOTTOM = Math.ceil(ORB_VISUAL_TOP + GLASS_SHADOW_Y + GLASS_SHADOW_BLUR + CAPTION_AIR);

/**
 * Muchia de jos a CARDULUI din pielea `hud` - mai jos decât bula de voce,
 * deliberat. Bula plutește peste un orb care dansează la energie maximă, deci
 * ține distanța calculată pe cazul cel mai înalt (`BUBBLE_BOTTOM`). Cardul de
 * text stă peste un orb în repaus; aceeași distanță acolo se citea ca un gol
 * fără sens între card și orb (feedback 11 aug 2026). Umbra cardului coboară
 * peste zona halo-ului - în regulă: umbra e întuneric difuz, orbul e lumină.
 */
const HUD_CARD_BOTTOM = Math.ceil(ORB_VISUAL_TOP + 8);

/**
 * Muchia de jos a BULEI de voce în pielea `hud` - mai jos decât pe pielea
 * implicită, la cererea proprietarului (11 aug 2026: distanța calculată se
 * citea ca un gol între transcript și orb).
 *
 * Renunță deliberat la termenul de umbră din `BUBBLE_BOTTOM`: rămân doar vârful
 * vizual al orbului și aerul liber, deci umbra sticlei poate atinge halo-ul.
 * Acceptat - în pielea asta umbra e întuneric difuz peste lumină, exact
 * compromisul făcut deja pentru card (`HUD_CARD_BOTTOM`). Ce NU se schimbă:
 * bula rămâne peste orb, nu peste el, iar cardul rămâne mai jos decât bula.
 */
const HUD_BUBBLE_BOTTOM = Math.ceil(ORB_VISUAL_TOP + CAPTION_AIR);

/**
 * Cerneala pe sticlă mată. Fixă, nu derivată din culoarea clientului: e text de
 * citit pe o suprafață aproape albă, iar o culoare de brand aplicată textului a
 * fost exact ce a făcut prototipul v1 să pară o reclamă. Culoarea clientului
 * trăiește în lumini (orb, halo, particule) - vezi paleta.
 */
const INK = "#262040";

/**
 * Cerneala pielii `hud`: alb-cenușiu pe sticlă închisă. Tot
 * fixă, din același motiv ca `INK` - culoarea clientului rămâne în lumini
 * (orbul, inelul, colțarele), nu în text.
 */
const HUD_INK = "#f5f6f7";
const HUD_DIM = "rgba(245,246,247,.62)";
/** Brațul unui colțar de vizor și grosimea lui. */
const HUD_BRACKET = 14;
const HUD_BRACKET_STROKE = 1.5;
/** Raza sticlei închise: colț tehnic, nu bulă. */
const HUD_RADIUS = 8;

/**
 * Aerul dintre rândurile cardului. Numit fiindcă intră în calculul de mai jos:
 * un `gap` schimbat cu mâna în CSS ar strica plafonul fără să se vadă.
 */
const CARD_GAP = 6;

/* ---------------- Geometria panoului `hud`: cardul trebuie SĂ ÎNCAPĂ ----------------
 *
 * Cardul din pielea `hud` NU e forma desenată în canvas (acolo nu se morfează
 * nimic), deci nu are ce căuta sub plafonul ei, `CARD_MAX_HEIGHT` = 140px. Cu
 * acel plafon, podeaua de citibilitate a mesajului (72px, pusă pe 11 aug 2026)
 * plus rândul de input depășeau cutia, iar `overflow: hidden` de pe card TĂIA
 * ultimul copil - adică exact rândul de jos (raport cu captură, 12 aug 2026).
 *
 * De-aceea plafonul pielii `hud` are o PODEA proprie, derivată din exact
 * termenii care ocupă înălțimea, nu dintr-un număr rotund: dacă cineva mărește
 * mâine podeaua mesajului sau înălțimea unui rând, plafonul crește cu ea.
 */
const HUD_PAD_Y = 12;
/** Podeaua de citibilitate a mesajului (~trei rânduri). */
const HUD_MSG_FLOOR = 72;
/** Cel mai înalt control dintr-un rând: pastila de voce. */
const HUD_ROW = 28;
/**
 * Cât trebuie să aibă cardul ca mesajul ȘI rândul de input să încapă amândouă.
 * Un singur rând și un singur spațiu între el și mesaj de pe 12 aug 2026: rândul
 * de unelte a dispărut, iar chevronul de istoric a devenit un buton absolut în
 * colț (vezi CSS) - un element absolut nu adaugă nicio înălțime, deci nu intră
 * în aritmetica asta.
 */
const HUD_CARD_FIT = HUD_MSG_FLOOR + CARD_GAP + HUD_ROW + 2 * HUD_PAD_Y;
/** Plafonul propriu-zis pe ecrane înalte - peste atât, panoul ar mânca pagina. */
const HUD_CARD_CEIL = 320;
/**
 * Lățimea panoului `hud` (cerere de proprietar, 12 aug 2026: 340px se citea
 * îngust pentru un răspuns cu carduri). Plafonată de fereastră cu aceeași
 * fracțiune ca înainte, deci pe telefon rămâne exact cât era.
 */
const HUD_CARD_WIDTH = 430;

export const ORBCHAT_CSS = `
/* This whole sheet lives in the orb-chat's own shadow root, on a host that is a
   SIBLING of .aig-root - never a descendant. A transform anywhere up the tree makes
   an ancestor the containing block for position:fixed descendants, across the shadow
   boundary too, and some shops (demo3/Shopify) do transform the launcher's container.
   As a sibling, "fixed bottom-centre" keeps meaning the window.
   No backticks anywhere in this file: it is a template literal, and one would end the
   string. Comments here are English because esbuild keeps template-string content
   verbatim - it ships to every customer's page (see CLAUDE.md). */
:host {
  font-family: system-ui, -apple-system, sans-serif !important;
  font-size: 14px !important; font-weight: 400 !important; font-style: normal !important;
  line-height: 1.66 !important; letter-spacing: normal !important; word-spacing: normal !important;
  text-transform: none !important; text-align: left !important; text-indent: 0 !important;
  text-shadow: none !important; white-space: normal !important; direction: ltr !important;
  color: ${INK} !important; visibility: visible !important;
}
:host(.aig-orbchat-hidden) { display: none !important; }
:host, :host *, :host *::before, :host *::after { box-sizing: border-box; }

/* The presence layer draws its own caption under the orb. In orb-chat the card IS the
   caption - the same words would otherwise appear twice, once as glass and once as a
   floating line. Hidden here rather than removed there, so the classic path keeps it. */
.aig-presence-caption { display: none !important; }

/* The orb is the button. The canvas above it cannot take the pointer (it covers half the
   viewport width), so the hit area is its own small square, exactly over the orb.
   --aig-lift (data-bottom-offset on the script tag, see sdk/index.ts): the same custom
   property the presence layer reads, set once on this shadow's host, so the whole
   ensemble - orb, waveform, caption, card, hit target, voice bubble - moves together. */
.aig-orb-hit {
  position: fixed !important; left: 50%; bottom: calc(20px + var(--aig-lift, 0px)); width: 64px; height: 64px; margin-left: -32px;
  padding: 0; border: none; background: transparent; cursor: pointer; z-index: 2147482992;
  appearance: none; -webkit-appearance: none; font-family: inherit;
}
.aig-orb-hit:focus-visible { outline: 2px solid var(--aig-orb-glow, #7c3aed); outline-offset: 2px; border-radius: 50%; }
/* Arrival (22 Aug 2026): on a freshly loaded page the orb enters at 1.5x and settles
   back to its size after about two seconds if nobody touches it - a cue that it is
   there, not a nag. The CSS "scale" property, not "transform": the presence layer
   keeps "transform" for its guidance shift, and the two must not fight. Origin at the bottom, so the orb
   grows upward from where it sits. No opacity involved: in a background tab
   transitions freeze, and a frozen scale is still a visible orb. */
.aig-presence-orb, .aig-orb-hit { transform-origin: 50% 100%; transition: scale .6s cubic-bezier(.2, .8, .2, 1); }
:host(.aig-orb-arrive) .aig-presence-orb, :host(.aig-orb-arrive) .aig-orb-hit { scale: 1.5; }
@media (prefers-reduced-motion: reduce) {
  .aig-presence-orb, .aig-orb-hit { transition: none; }
}

/* The one explicit way out of a voice conversation, ON the transcript bubble (owner
   decision, 25 Aug 2026): the bubble is open for the whole call - it carries this
   control - and the orb toggles the bubble's visibility instead of ending the call.
   A header row of its own rather than an absolute corner pill: the transcript under it
   scrolls, and a floating control over moving words would cover exactly the newest ones. */
.aig-orbbubble-top { display: flex; justify-content: flex-end; flex: none; }
.aig-voice-stop {
  display: inline-flex; align-items: center; gap: 5px; padding: 5px 11px; border: none; border-radius: 999px;
  background: rgba(38,32,64,.08);
  color: ${INK}; font-family: inherit; font-size: 12px; font-weight: 500; line-height: 1;
  cursor: pointer; appearance: none; -webkit-appearance: none;
}
.aig-voice-stop:hover { background: rgba(38,32,64,.14); }
.aig-voice-stop:focus-visible { outline: 2px solid var(--aig-orb-glow, #7c3aed); outline-offset: 2px; }
.aig-voice-stop svg { width: 11px; height: 11px; display: block; fill: currentColor; }
:host(.aig-skin-hud) .aig-voice-stop {
  background: rgba(255,255,255,.10); color: ${HUD_INK}; box-shadow: none;
}
/* No canvas (reduced motion, or a browser without 2D context): the orb still has to be
   there and still has to be pressable, so it is drawn in CSS - a still jewel, no motion. */
.aig-orb-hit--flat::before {
  content: ""; display: block; width: 30px; height: 30px; margin: 17px auto; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, var(--aig-orb-core, #c9b8ff), var(--aig-orb-glow, #7c3aed) 65%, var(--aig-orb-deep, #4c1d95));
  box-shadow: 0 0 18px 3px var(--aig-orb-halo, rgba(124,58,237,.28));
}

/* The card: text floating on the glass drawn in canvas, so no background of its own.
   Fade only - the shape's own morph is the motion, and two motions at once read as noise. */
.aig-orbcard {
  position: fixed !important; left: 50%; bottom: calc(${CONTENT_BOTTOM}px + var(--aig-lift, 0px));
  width: ${CONTENT_WIDTH}px; margin-left: -${CONTENT_WIDTH / 2}px;
  max-height: ${CONTENT_MAX_HEIGHT}px; overflow: hidden;
  display: flex; flex-direction: column; justify-content: center; gap: ${CARD_GAP}px;
  z-index: 2147482993; opacity: 0; pointer-events: none; transition: opacity .28s ease;
}
.aig-orbcard--on { opacity: 1; pointer-events: auto; }
/* Frosted glass drawn in CSS rather than canvas. Two callers, one recipe: the flat card
   (no canvas - reduced motion, or no 2D context) and the voice bubble, which always needs
   it because during a voice call the canvas is busy being an orb, not a card. */
.aig-orbcard--flat, .aig-orbbubble {
  padding: 14px 18px; border-radius: 22px;
  background: linear-gradient(180deg, var(--aig-glass-top, #f6f3ff), var(--aig-glass-bottom, #efe9fb));
  box-shadow: 0 ${GLASS_SHADOW_Y}px ${GLASS_SHADOW_BLUR}px rgba(10,6,24,.18);
}
.aig-orbcard--flat { max-height: ${CARD_MAX_HEIGHT}px; }
@media (prefers-reduced-motion: reduce) {
  .aig-orbcard, .aig-orbbubble { transition: opacity .12s linear; }
}

/* The voice bubble: while a voice conversation is live the orb does NOT morph - it stays an
   orb and dances to the audio, because that is the show. The transcript floats above the
   whole ensemble instead, in the same glass, and never covers it (see BUBBLE_BOTTOM).
   No input field - the visitor is talking. The one control on it is the Stop pill in its
   top row; the orb toggles the bubble's visibility (owner decision, 25 Aug 2026). */
.aig-orbbubble {
  position: fixed !important; left: 50%; bottom: calc(${BUBBLE_BOTTOM}px + var(--aig-lift, 0px));
  width: min(${CARD_WIDTH}px, 84vw); transform: translateX(-50%);
  max-height: ${CARD_MAX_HEIGHT}px;
  display: flex; flex-direction: column; gap: 4px;
  z-index: 2147482993; opacity: 0; pointer-events: none; transition: opacity .28s ease;
}
.aig-orbbubble--on { opacity: 1; pointer-events: auto; }
/* The cap is on the bubble; the scrolling happens inside it, so the glass never grows past
   its ceiling and the shadow stays where the geometry above assumes it is. */
.aig-orbbubble-scroll { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
/* Nothing here is editable, so the card's text cursor would be a lie. */
.aig-orbbubble .aig-orbcard-msg { cursor: default; }

/* Ink on frosted glass: no shadows, no bold. The air does the premium, not the weight. */
.aig-orbcard-msg { font-size: 14px; font-weight: 500; line-height: 1.66; letter-spacing: .002em; color: ${INK}; cursor: text; }
.aig-orbcard-msg .aig-dim { color: rgba(38,32,64,.52); font-weight: 400; }
/* Thinking is an action of its own, not a decorated wait: the question steps aside and a
   single light sweeps the word. */
.aig-orbcard-msg .aig-think {
  display: block; width: 100%; text-align: center; font-size: 13px; font-style: italic; font-weight: 500;
  letter-spacing: .06em;
  background: linear-gradient(90deg, rgba(38,32,64,.4) 25%, rgba(38,32,64,.95) 50%, rgba(38,32,64,.4) 75%);
  background-size: 200% 100%; -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent; color: transparent;
  animation: aig-shimmer 1.8s linear infinite;
}
@keyframes aig-shimmer { from { background-position: 200% 0; } to { background-position: 0% 0; } }
.aig-orbcard-msg .aig-hint { display: block; margin-top: 7px; font-size: 10.5px; letter-spacing: .02em; color: rgba(38,32,64,.45); }
/* Word by word: the answer settles at reading speed instead of landing as a wall. */
.aig-orbcard-msg .aig-w { opacity: 0; display: inline-block; animation: aig-word-in .35s forwards; }
.aig-orbcard-msg .aig-w--shown { opacity: 1; animation: none; }
@keyframes aig-word-in { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
@media (prefers-reduced-motion: reduce) {
  .aig-orbcard-msg .aig-w { opacity: 1; animation: none; }
  .aig-orbcard-msg .aig-think { animation: none; -webkit-text-fill-color: ${INK}; color: ${INK}; background: none; }
}

/* Content beyond the text (product cards, notices) pushes the height up to the cap and
   then scrolls inside the glass - the shape never grows past its ceiling. */
.aig-orbcard-body { overflow-y: auto; overscroll-behavior: contain; }
.aig-orbcard-note { font-size: 10.5px; line-height: 1.4; color: rgba(38,32,64,.55); text-align: center; }

/* The history chevron floats in the card's top-right corner, over whatever is under it.
   It used to sit in a row of its own under the input row; once the voice pill moved into
   the input row that row held this one button and cost the panel a whole line of height
   (owner decision, 12 Aug 2026). Absolute, so it adds NO height at all - the card's fit
   arithmetic further down counts the message and the input row only.
   Ghost button - colour lives in the lights, never in the chrome. */
/* The close button owns the top-right corner - the conventional spot for leaving - and
   the history chevron sits one button-width to its left (24 Aug 2026: closing moved from
   "click anywhere outside" to this explicit control, so it must be where fingers expect it). */
.aig-orbcard-close-btn { position: absolute; top: 2px; right: 2px; z-index: 2; }
.aig-orbcard-history-btn { position: absolute; top: 2px; right: 28px; z-index: 2; }
/* Inside the glass rather than on its rim: the flat card and the HUD panel both carry
   real padding, and a control sitting on a 22px corner radius reads as falling off. */
.aig-orbcard--flat .aig-orbcard-close-btn { top: 6px; right: 8px; }
.aig-orbcard--flat .aig-orbcard-history-btn { top: 6px; right: 36px; }
/* Open = the chevron turns over and points the way back. The flip IS the state, together
   with the aria-label (both driven from orbchat/index.ts) - a filled disc floating over
   the answer would be the loudest thing on the surface, for the quietest control on it. */
.aig-orbcard-history-btn svg { transition: transform .2s ease; }
.aig-ibtn--flip svg { transform: rotate(180deg); }
@media (prefers-reduced-motion: reduce) {
  .aig-orbcard-history-btn svg { transition: none; }
}
/* Nothing runs under the floating button: the first line of the answer and the first line
   of the unfolded history keep a lane clear on the right. The voice bubble has no such
   button, so its own text is untouched - this is scoped to the card. */
.aig-orbcard .aig-orbcard-msg, .aig-orbcard .aig-orbcard-history { padding-right: 28px; }
.aig-ibtn {
  flex: none; width: 26px; height: 26px; border-radius: 50%; border: none; padding: 0; cursor: pointer;
  display: flex; align-items: center; justify-content: center; background: transparent;
  color: rgba(38,32,64,.55); transition: color .2s, background .2s;
  appearance: none; -webkit-appearance: none; font-family: inherit;
}
.aig-ibtn:hover { color: ${INK}; background: rgba(38,32,64,.08); }
.aig-ibtn:focus-visible { outline: 2px solid var(--aig-orb-glow, #7c3aed); outline-offset: -2px; }
.aig-ibtn svg { width: 15px; height: 15px; display: block; }
/* Live states earn the only colour in the chrome: a microphone the visitor forgot is on
   is a real problem, not a styling detail. */
.aig-ibtn--live { color: var(--aig-orb-deep, #4c1d95); background: var(--aig-orb-tint, rgba(124,58,237,.18)); }
.aig-ibtn--live:hover { background: var(--aig-orb-tint, rgba(124,58,237,.18)); }

/* The same outline becomes a transparent field: no box, no border - the glass is the box. */
.aig-orbcard-input { display: none; align-items: center; gap: 6px; }
.aig-orbcard--input .aig-orbcard-input { display: flex; }
.aig-orbcard-input input {
  flex: 1; min-width: 0; background: transparent; border: none; outline: none; padding: 0;
  color: ${INK}; font-family: inherit; font-size: 13.5px; font-weight: 500;
}
.aig-orbcard-input input::placeholder { color: rgba(38,32,64,.42); font-weight: 400; opacity: 1; }

/* History on demand: the same glass, unfolded. Default is the last exchange only - a
   scrollback the visitor did not ask for turns a presence back into a chat window. */
.aig-orbcard-history { display: none; flex-direction: column; gap: 7px; overflow-y: auto; overscroll-behavior: contain; }
.aig-orbcard--history .aig-orbcard-history { display: flex; }
.aig-orbcard--history .aig-orbcard-msg, .aig-orbcard--history .aig-orbcard-body { display: none; }
.aig-orbcard-turn { font-size: 12.5px; line-height: 1.5; }
.aig-orbcard-turn--user { color: rgba(38,32,64,.52); font-style: italic; }
.aig-orbcard-turn--agent { color: ${INK}; }
.aig-orbcard-poweredby { margin-top: 2px; text-align: center; font-size: 9.5px; }
.aig-orbcard-poweredby a { color: rgba(38,32,64,.42); text-decoration: none; }
.aig-orbcard-poweredby a:hover { text-decoration: underline; }
/* The foot of the history carries the attribution and nothing else: since 12 Aug 2026
   there is no start-over button on the glass. */
.aig-orbcard-foot { display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 2px; }

/* Product cards render INSIDE the glass, under the text. They keep their own markup
   (commerce/cards.ts) and only lose the shop-panel chrome: on frosted glass a white box
   with a border reads as a second surface. One row that scrolls sideways, because the
   card's height is capped and a stack would eat the answer.
   The voice bubble is the same glass, so it gets the same treatment. */
.aig-orbcard .aig-cards, .aig-orbbubble .aig-cards { flex-direction: row; gap: 6px; overflow-x: auto; overscroll-behavior-x: contain; padding-bottom: 2px; }
.aig-orbcard .aig-card, .aig-orbbubble .aig-card { flex: 0 0 auto; width: 190px; background: rgba(255,255,255,.6); border-color: rgba(38,32,64,.10); }
.aig-orbcard .aig-card-title, .aig-orbcard .aig-card-price,
.aig-orbbubble .aig-card-title, .aig-orbbubble .aig-card-price { color: ${INK}; }
.aig-orbcard .aig-card-note, .aig-orbcard .aig-card-variant,
.aig-orbbubble .aig-card-note, .aig-orbbubble .aig-card-variant { color: rgba(38,32,64,.55); }

/* No rules for the comparison here on purpose: since 11 Aug 2026 it never renders inside
   the card or the voice bubble. It opens its own centred modal over the page, on every
   surface, so it carries all of its own styling (commerce/compare.ts). */

/* ====================== HUD skin - the orb-chat's only look ======================
   A dark instrument surface. Everything below is scoped to :host(.aig-skin-hud),
   a class the orb-chat host always carries (since 19 Aug 2026 there is no other
   skin); the classic panel never renders any of it.
   Two structural differences from the default skin, both in orbchat/index.ts:
   the orb never morphs into the card (the canvas always draws an orb), and the card
   therefore floats ABOVE the whole orb ensemble at the voice bubble's height, so it
   never covers the orb. The orb stays visible and breathing underneath - in this skin
   it reads as the assistant's core rather than as the material the card is made of.
   Restraint is the rule here, not decoration: this sits on someone else's shop. Nothing
   on the card flashes, pulses or loops - the only continuous motion in the whole skin is
   the orb's own breathing and the faint rotation of its ring (presence/orb.ts). */
:host(.aig-skin-hud) { color: ${HUD_INK} !important; }

/* The card takes the bubble's geometry: same bottom edge, same centring, so the two
   glass surfaces of this skin are the same object in two moments, and neither of them
   ever sits on top of the orb.
   Height is NOT the canvas shape's ceiling here - nothing morphs in this skin, and that
   ceiling (140px) could not hold the message floor plus the rows under it, so the last
   child was clipped away (owner screenshot, 12 Aug 2026). The floor of this
   max-height is computed from those very rows, so the bottom row is always on screen;
   above it the panel grows with the window and stops before it eats the page. */
:host(.aig-skin-hud) .aig-orbcard {
  bottom: calc(${HUD_CARD_BOTTOM}px + var(--aig-lift, 0px));
  width: min(${HUD_CARD_WIDTH}px, 84vw); margin-left: 0; transform: translateX(-50%);
  max-height: max(${HUD_CARD_FIT}px, min(${HUD_CARD_CEIL}px, 62vh));
  justify-content: flex-start;
}
/* The voice bubble comes down too, and further than the base skin allows: only the orb's
   visual top and the free air are kept, the shadow reach is dropped (see HUD_BUBBLE_BOTTOM).
   Still above the orb, still below nothing - the card stays closer than this. */
:host(.aig-skin-hud) .aig-orbbubble { bottom: calc(${HUD_BUBBLE_BOTTOM}px + var(--aig-lift, 0px)); }
/* The base card centres its content inside the canvas-drawn shape and clips the rest -
   right for a morphing shape, wrong for a real panel: a long answer lost its FIRST lines
   at the top and its controls (the voice pill!) at the bottom (owner report, 11 Aug 2026).
   Here the text scrolls inside the glass and every control stays on screen.
   The floor (roughly three lines) is the other half of the same problem: on a phone with
   the keyboard open a one-line answer collapsed the panel to a sliver and the answer was
   unreadable (tester report, 11 Aug 2026). It is a floor, not a height - long answers
   still grow to the cap and scroll inside the glass. */
:host(.aig-skin-hud) .aig-orbcard-msg { overflow-y: auto; flex: 0 1 auto; min-height: ${HUD_MSG_FLOOR}px; }
:host(.aig-skin-hud) .aig-orbcard-input { flex: none; }
/* The floating chevron sits in the panel's padding band, clear of the bezel. The corner
   brackets are top-left and bottom-right, so this corner is free. */
:host(.aig-skin-hud) .aig-orbcard-close-btn { top: 4px; right: 6px; }
:host(.aig-skin-hud) .aig-orbcard-history-btn { top: 4px; right: 34px; }
/* A column flex item will not shrink below its own content unless it is told to: without
   this, one product card - or an unfolded history - pushed the rows under it out of the
   box, and the card clips (overflow: hidden above). They scroll inside the glass instead. */
:host(.aig-skin-hud) .aig-orbcard-body, :host(.aig-skin-hud) .aig-orbcard-history {
  flex: 0 1 auto; min-height: 0;
}
/* One glass recipe for both surfaces: near-black rather than tinted, because a dark
   panel that carries the brand hue reads as a coloured box, not as a screen. The colour
   lives in the lights - the orb, its ring, the corner brackets. */
:host(.aig-skin-hud) .aig-orbcard, :host(.aig-skin-hud) .aig-orbbubble {
  padding: 12px 16px; border-radius: ${HUD_RADIUS}px;
  background: linear-gradient(180deg, rgba(22,22,27,.88), rgba(10,10,13,.85));
  -webkit-backdrop-filter: blur(16px) saturate(140%);
  backdrop-filter: blur(16px) saturate(140%);
  border: 1px solid rgba(255,255,255,.14);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.08), 0 10px 30px rgba(0,0,0,.5);
}

/* Viewfinder brackets, top-left and bottom-right only. Two corners read as a frame;
   four read as a box, and a box around a small card is chrome the visitor has to look
   past. They sit in the padding, never over the text, and never take the pointer. */
:host(.aig-skin-hud) .aig-orbcard::before, :host(.aig-skin-hud) .aig-orbcard::after,
:host(.aig-skin-hud) .aig-orbbubble::before, :host(.aig-skin-hud) .aig-orbbubble::after {
  content: ""; position: absolute; width: ${HUD_BRACKET}px; height: ${HUD_BRACKET}px;
  pointer-events: none; border: 0 solid var(--aig-orb-glow, #7c3aed); opacity: .75;
}
:host(.aig-skin-hud) .aig-orbcard::before, :host(.aig-skin-hud) .aig-orbbubble::before {
  top: -1px; left: -1px;
  border-top-width: ${HUD_BRACKET_STROKE}px; border-left-width: ${HUD_BRACKET_STROKE}px;
  border-top-left-radius: ${HUD_RADIUS}px;
}
:host(.aig-skin-hud) .aig-orbcard::after, :host(.aig-skin-hud) .aig-orbbubble::after {
  bottom: -1px; right: -1px;
  border-bottom-width: ${HUD_BRACKET_STROKE}px; border-right-width: ${HUD_BRACKET_STROKE}px;
  border-bottom-right-radius: ${HUD_RADIUS}px;
}

/* Typing: the card TAKES THE ORB'S PLACE. On a phone with the keyboard open the visible
   page is a strip, and spending its lower third on an orb and a waveform left the answer
   unreadable (tester report with screenshot, 11 Aug 2026). While the visitor is reading
   and writing, the orb has nothing to say - so it steps aside and the panel sits where it
   was, at the bottom edge. This is TEXT ONLY: the class is never on during a voice call
   (orbchat/index.ts toggles it in setMode/setVoiceActive), because there the dancing orb
   is the whole show and the transcript floats above it, untouched. */
:host(.aig-skin-hud.aig-hud-chatting) .aig-presence,
:host(.aig-skin-hud.aig-hud-chatting) .aig-orb-hit { display: none !important; }
:host(.aig-skin-hud.aig-hud-chatting) .aig-orbcard { bottom: calc(20px + var(--aig-lift, 0px)); }
/* The UA's light scrollbar punches a white slot through the dark glass. */
:host(.aig-skin-hud) .aig-orbcard-body, :host(.aig-skin-hud) .aig-orbbubble-scroll,
:host(.aig-skin-hud) .aig-orbcard-history, :host(.aig-skin-hud) .aig-orbcard-msg {
  scrollbar-width: thin; scrollbar-color: rgba(255,255,255,.22) transparent;
}

/* Ink on dark. Body text stays at 14px: this surface is read at arm's length on a
   phone, and a HUD that has to be squinted at is a costume, not an interface. */
:host(.aig-skin-hud) .aig-orbcard-msg { color: ${HUD_INK}; font-size: 14px; }
:host(.aig-skin-hud) .aig-orbcard-msg .aig-dim { color: ${HUD_DIM}; }
:host(.aig-skin-hud) .aig-orbcard-msg .aig-hint { color: rgba(245,246,247,.45); }
:host(.aig-skin-hud) .aig-orbcard-note { color: ${HUD_DIM}; }
:host(.aig-skin-hud) .aig-orbcard-turn--agent { color: ${HUD_INK}; }
:host(.aig-skin-hud) .aig-orbcard-turn--user { color: ${HUD_DIM}; }
:host(.aig-skin-hud) .aig-orbcard-input input { color: ${HUD_INK}; font-size: 14px; }
:host(.aig-skin-hud) .aig-orbcard-input input::placeholder { color: rgba(245,246,247,.42); }
:host(.aig-skin-hud) .aig-ibtn { color: ${HUD_DIM}; }
:host(.aig-skin-hud) .aig-ibtn:hover { color: ${HUD_INK}; background: rgba(255,255,255,.10); }
:host(.aig-skin-hud) .aig-ibtn--live { color: ${HUD_INK}; background: rgba(255,255,255,.12); }
:host(.aig-skin-hud) .aig-ibtn--live:hover { background: rgba(255,255,255,.12); }
:host(.aig-skin-hud) .aig-orbcard-poweredby a { color: rgba(245,246,247,.38); }
/* The shimmer inverts: light sweeping across dim text instead of dark across pale.
   Same single pass, same word-by-word reveal above it - neither is touched here. */
:host(.aig-skin-hud) .aig-orbcard-msg .aig-think {
  background: linear-gradient(90deg, rgba(245,246,247,.35) 25%, rgba(245,246,247,.95) 50%, rgba(245,246,247,.35) 75%);
  background-size: 200% 100%; -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent; color: transparent;
}

/* Product cards inside the dark glass: lifted out of the surface by light, not by a
   white box - a white card here would punch a hole in the panel. */
:host(.aig-skin-hud) .aig-orbcard .aig-card, :host(.aig-skin-hud) .aig-orbbubble .aig-card {
  background: rgba(255,255,255,.06); border-color: rgba(255,255,255,.12);
}
:host(.aig-skin-hud) .aig-orbcard .aig-card-title, :host(.aig-skin-hud) .aig-orbcard .aig-card-price,
:host(.aig-skin-hud) .aig-orbbubble .aig-card-title, :host(.aig-skin-hud) .aig-orbbubble .aig-card-price { color: ${HUD_INK}; }
:host(.aig-skin-hud) .aig-orbcard .aig-card-note, :host(.aig-skin-hud) .aig-orbcard .aig-card-variant,
:host(.aig-skin-hud) .aig-orbbubble .aig-card-note, :host(.aig-skin-hud) .aig-orbbubble .aig-card-variant { color: ${HUD_DIM}; }

/* The voice pill (owner feedback, 11 Aug 2026): a bare 15px icon did not read as "start
   a voice conversation" - the single most important control on a voice-first surface was
   invisible. In this skin the same button wears its label. The label span exists in every
   skin; only this skin displays it, so the classic surface keeps its icon-only button.
   Since 12 Aug 2026 the button sits IN the input row, between the field and send, where
   the dictation microphone used to be. It keeps the pill shape - it is still the one
   control that has to be found - but wears the short label, because the full sentence
   inside an input row would leave the field a stub. The full sentence is still what a
   screen reader gets: it stays on aria-label (orbchat/index.ts). */
.aig-vlabel { display: none; }
:host(.aig-skin-hud) .aig-ibtn-voice {
  width: auto; height: ${HUD_ROW}px; border-radius: 999px; padding: 0 10px 0 8px; gap: 5px;
  border: 1px solid rgba(255,255,255,.16); background: rgba(255,255,255,.06);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.10);
  color: ${HUD_INK};
}
:host(.aig-skin-hud) .aig-ibtn-voice .aig-vlabel {
  display: inline; font-size: 12px; font-weight: 500; letter-spacing: .04em; white-space: nowrap;
}
:host(.aig-skin-hud) .aig-ibtn-voice:hover { background: rgba(255,255,255,.12); }
/* Live = a steady brightened bezel, not a fill and not a pulse: an open microphone is
   state the visitor must not lose track of, told once and held still. */
:host(.aig-skin-hud) .aig-ibtn-voice.aig-ibtn--live {
  border-color: var(--aig-orb-glow, #8e939b);
  background: rgba(255,255,255,.10);
  color: ${HUD_INK};
}
/* Assembling, not fading in: the panel arrives from just below with a short settle.
   It runs once per opening (the animation restarts every time the --on class is added)
   and then nothing on this surface moves again. */
:host(.aig-skin-hud) .aig-orbcard--on, :host(.aig-skin-hud) .aig-orbbubble--on {
  animation: aig-hud-in .2s cubic-bezier(.2,.8,.3,1.05) both;
}
@keyframes aig-hud-in {
  from { opacity: 0; transform: translate(-50%, 6px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}
/* The brackets draw on with the panel - scale from their own corner, 60ms apart, then
   freeze forever. Assembly felt once per opening, never a loop. */
:host(.aig-skin-hud) .aig-orbcard--on::before, :host(.aig-skin-hud) .aig-orbbubble--on::before {
  transform-origin: top left;
  animation: aig-bracket-in .24s cubic-bezier(.2,.8,.3,1.05) both;
}
:host(.aig-skin-hud) .aig-orbcard--on::after, :host(.aig-skin-hud) .aig-orbbubble--on::after {
  transform-origin: bottom right;
  animation: aig-bracket-in .24s cubic-bezier(.2,.8,.3,1.05) .06s both;
}
@keyframes aig-bracket-in {
  from { opacity: 0; transform: scale(0); }
  to { opacity: .75; transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  :host(.aig-skin-hud) .aig-orbcard--on, :host(.aig-skin-hud) .aig-orbbubble--on { animation: none; }
  :host(.aig-skin-hud) .aig-orbcard--on::before, :host(.aig-skin-hud) .aig-orbbubble--on::before,
  :host(.aig-skin-hud) .aig-orbcard--on::after, :host(.aig-skin-hud) .aig-orbbubble--on::after { animation: none; }
  :host(.aig-skin-hud) .aig-orbcard-msg .aig-think {
    -webkit-text-fill-color: ${HUD_INK}; color: ${HUD_INK}; background: none;
  }
}
`;

/**
 * Chevron: istoricul se desfășoară în sus, deci cât e strâns arată în sus. Când
 * e deschis, CSS-ul îl întoarce (`aig-ibtn--flip`) - o rotație, nu un al doilea
 * `path`: aceeași pictogramă, jumătate din cod.
 */
const CHEVRON_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m18 15-6-6-6 6"/></svg>';
const SEND_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4z"/></svg>';
/**
 * Voice: a sound wave, not a microphone. The dictation microphone that used to sit next
 * to it is gone (12 Aug 2026): two microphone-shaped controls one beside the other read
 * as the same act, and Romanian dictation was poor enough not to be worth the confusion.
 */
/** X-ul de închidere: singura cale de a strânge cardul, alături de orb și Escape. */
const CLOSE_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';
const VOICE_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" aria-hidden="true">' +
  '<path d="M4 10v4"/><path d="M8 6v12"/><path d="M12 3v18"/><path d="M16 6v12"/><path d="M20 10v4"/></svg>';

export interface CardParts {
  card: HTMLDivElement;
  message: HTMLDivElement;
  body: HTMLDivElement;
  history: HTMLDivElement;
  inputRow: HTMLDivElement;
  input: HTMLInputElement;
  sendBtn: HTMLButtonElement;
  historyBtn: HTMLButtonElement;
  closeBtn: HTMLButtonElement;
  voiceBtn: HTMLButtonElement;
  /**
   * Eticheta din pastila de voce (doar pielea `hud` o arată - vezi CSS).
   * Referință ținută ca `setVoiceActive` să poată comuta textul între
   * `voiceStart` și `voiceEnd` fără să caute în DOM.
   */
  voiceLabel: HTMLSpanElement;
}

function iconButton(className: string, icon: string, label: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.className = `aig-ibtn ${className}`;
  btn.type = "button";
  btn.innerHTML = icon;
  btn.setAttribute("aria-label", label);
  btn.title = label;
  return btn;
}

/** Pătratul de stop: singura cale explicită de a încheia conversația vocală. */
const STOP_ICON =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="7" width="10" height="10" rx="2"/></svg>';

/** Butonul-orb. Separat de card: el există și când cardul e strâns. */
export function buildHitArea(flat: boolean, copy: ChromeCopy): HTMLButtonElement {
  const hit = document.createElement("button");
  hit.className = `aig-orb-hit${flat ? " aig-orb-hit--flat" : ""}`;
  hit.type = "button";
  hit.setAttribute("aria-label", copy.orbAsk);
  return hit;
}

export function buildCard(flat: boolean, copy: ChromeCopy): CardParts {
  const card = document.createElement("div");
  card.className = `aig-orbcard${flat ? " aig-orbcard--flat" : ""}`;

  const message = document.createElement("div");
  message.className = "aig-orbcard-msg";
  // `role="status"`: în orb-chat ăsta e SINGURUL text al conversației - un
  // cititor de ecran nu are de unde afla altfel ce a răspuns asistentul.
  message.setAttribute("role", "status");

  const body = document.createElement("div");
  body.className = "aig-orbcard-body";

  const history = document.createElement("div");
  history.className = "aig-orbcard-history";
  history.setAttribute("role", "log");
  history.setAttribute("aria-label", copy.historyLabel);

  const input = document.createElement("input");
  input.type = "text";
  input.autocomplete = "off";
  input.placeholder = copy.orbPlaceholder;
  input.setAttribute("aria-label", copy.orbPlaceholder);
  input.maxLength = 1000;

  const sendBtn = iconButton("aig-ibtn-send", SEND_ICON, copy.send);
  const historyBtn = iconButton(
    "aig-ibtn-history aig-orbcard-history-btn",
    CHEVRON_ICON,
    copy.historyShow,
  );
  const closeBtn = iconButton("aig-ibtn-close aig-orbcard-close-btn", CLOSE_ICON, copy.close);
  const voiceBtn = iconButton("aig-ibtn-voice", VOICE_ICON, copy.voiceStart);
  // Eticheta pastilei: doar pielea `hud` o desenează (feedback 11 aug 2026 -
  // pictograma singură era invizibilă ca „buton de start conversație"). În
  // pielea clasică span-ul stă ascuns din CSS, deci butonul rămâne pictograma
  // de 26px de dinainte, pixel cu pixel. Textul din pastilă e cel SCURT: în
  // rândul de input, propoziția întreagă ar fi lăsat câmpul un ciot. Propoziția
  // rămâne pe `aria-label`, deci cititorul de ecran n-a pierdut nimic.
  const voiceLabel = document.createElement("span");
  voiceLabel.className = "aig-vlabel";
  voiceLabel.textContent = copy.voiceStartShort;
  voiceBtn.appendChild(voiceLabel);
  // Ascuns până când există chiar o cale vocală (vezi `onVoiceToggle`): un buton
  // care nu poate face nimic e mai rău decât lipsa lui.
  voiceBtn.hidden = true;

  // Rândul de scris: câmpul, vocea, trimiterea. Vocea stă unde stătea microfonul
  // de dictare (12 aug 2026): conversația vocală e acțiunea principală a
  // suprafeței, deci stă în calea degetului, nu într-un rând de sub ea.
  const inputRow = document.createElement("div");
  inputRow.className = "aig-orbcard-input";
  inputRow.append(input, voiceBtn, sendBtn);

  // Fără rând de stare (11 aug 2026) și, de pe 12 aug 2026, fără rând de unelte:
  // după ce pastila de voce a intrat în rândul de input, uneltele rămăseseră un
  // rând întreg pentru un singur chevron. Chevronul plutește acum absolut în
  // colțul din dreapta-sus (vezi CSS) - ultimul în DOM tocmai ca tastatura să
  // ajungă întâi la câmpul de scris, poziția lui nedepinzând de ordine.
  card.append(message, body, history, inputRow, historyBtn, closeBtn);
  return { card, message, body, history, inputRow, input, sendBtn, historyBtn, closeBtn, voiceBtn, voiceLabel };
}

export interface BubbleParts {
  bubble: HTMLDivElement;
  message: HTMLDivElement;
  body: HTMLDivElement;
  /**
   * Cutia care derulează. Expusă fiindcă transcriptul trebuie ținut lipit de
   * capătul de JOS: bula are plafon, iar o replică lungă (sau un rând de
   * carduri sub ea) împinge cuvintele noi sub muchie, unde nimeni nu le vede.
   * Vocea nu are scrollback - ce nu se vede acum s-a pierdut.
   */
  scroll: HTMLDivElement;
  /** Pastila „Încheie" - singura cale explicită de a opri conversația vocală. */
  stopBtn: HTMLButtonElement;
}

/**
 * Bula de voce: aceleași clase de conținut ca ale cardului (`aig-orbcard-msg`,
 * `aig-orbcard-body`), deci aceeași cerneală, aceeași dezvăluire cuvânt cu
 * cuvânt, aceleași carduri de produs - un al doilea vocabular vizual ar fi al
 * doilea obiect, iar aici e același obiect, doar plutind.
 *
 * Fără câmp de scris: vizitatorul VORBEȘTE. Singurul control e pastila „Încheie"
 * din colțul ei (cerere de proprietar, 25 aug 2026): orbul nu mai oprește
 * conversația - el doar comută vizibilitatea bulei - deci sticla pe care se
 * uită omul când citește e și locul de unde poate opri.
 */
export function buildBubble(copy: ChromeCopy): BubbleParts {
  const bubble = document.createElement("div");
  bubble.className = "aig-orbbubble";

  // Rândul de sus: doar pastila de stop, aliniată dreapta. Deasupra derulării,
  // ca să rămână pe sticlă și când transcriptul curge sub plafon.
  const top = document.createElement("div");
  top.className = "aig-orbbubble-top";
  const stopBtn = document.createElement("button");
  stopBtn.className = "aig-voice-stop";
  stopBtn.type = "button";
  stopBtn.innerHTML = STOP_ICON;
  stopBtn.setAttribute("aria-label", copy.voiceEnd);
  stopBtn.title = copy.voiceEnd;
  const label = document.createElement("span");
  label.textContent = copy.voiceEndShort;
  stopBtn.appendChild(label);
  top.appendChild(stopBtn);

  const message = document.createElement("div");
  message.className = "aig-orbcard-msg";
  message.setAttribute("role", "status");

  const body = document.createElement("div");
  body.className = "aig-orbcard-body";

  const scroll = document.createElement("div");
  scroll.className = "aig-orbbubble-scroll";
  scroll.append(message, body);

  bubble.append(top, scroll);
  return { bubble, message, body, scroll, stopBtn };
}
